import type { Row } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { TypewriterWorkflowName } from "./TypewriterWorkflowName";

const Container = styled("span")`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: text;
  border-radius: 4px;
  padding: 2px 4px;
  margin: 0 -4px;

  &::after {
    content: attr(data-value);
    visibility: hidden;
    white-space: nowrap;
    font: inherit;
    pointer-events: none;
  }

  &:hover {
    background: var(--alpha8);
  }
`;

const DisplayText = styled("span")`
  position: absolute;
  top: 50%;
  left: 4px;
  transform: translateY(-50%);
`;

const NameInput = styled("input")`
  position: absolute;
  top: 50%;
  left: 4px;
  transform: translateY(-50%);
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  outline: none;
`;

type Props = {
  workflow: Row["workflows"];
  readOnly?: boolean;
};

export function EditableWorkflowName({ workflow, readOnly }: Props) {
  const { id: workflowId, name } = workflow;
  const z = useZero();
  const [editingName, setEditingName] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const isEditing = editingName !== undefined;
  const displayName = editingName ?? name;

  const handleSave = () => {
    const trimmed = editingName?.trim();
    if (trimmed && trimmed !== name) {
      z.mutate(sharedMutators.renameWorkflow({ workflowId, name: trimmed }));
    }
    setEditingName(undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setEditingName(undefined);
    }
  };

  return (
    <Container
      data-value={displayName || " "}
      onClick={() => {
        if (!isEditing && !readOnly) {
          setEditingName(name);
          requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
          });
        }
      }}
    >
      <DisplayText style={{ visibility: isEditing ? "hidden" : "visible" }}>
        <TypewriterWorkflowName name={name} />
      </DisplayText>
      <NameInput
        ref={inputRef}
        style={{ visibility: isEditing ? "visible" : "hidden" }}
        value={displayName}
        size={1}
        tabIndex={isEditing ? 0 : -1}
        onChange={(e) => setEditingName(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
      />
    </Container>
  );
}
